import React, { useState } from 'react';
import moment from 'moment';
import { X, GitCompare } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { useSocket } from '../Context/SocketContext';
import { infoUser } from '../utils/auth';

const STATUSES = [
    { key: 'done', label: 'Validé' },
    { key: 'paid', label: 'À venir' },
    { key: 'cancel', label: 'Annulé' },
    { key: 'tous', label: 'Tous' },
]
const TYPES = [
    { key: null, label: 'Tous' },
    { key: 'streaming', label: 'Streaming' },
    { key: 'sport', label: 'Sport' },
    { key: 'premium', label: 'Premium' },
    { key: 'vitrine', label: 'Vitrine' },
]

const MONTHS = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc']
const BASE_YEAR = 2026

const ModalComparateur = ({ onClose }) => {
    const { socket } = useSocket()
    const userToken = infoUser()
    const today = moment().format('YYYY-MM-DD')
    const now = moment()
    const currentYear = now.year()

    const years = []
    for (let y = BASE_YEAR; y <= Math.max(currentYear, BASE_YEAR); y++) years.push(y)

    const [selectedYear, setSelectedYear] = useState(Math.max(currentYear, BASE_YEAR))
    const [start, setStart] = useState(moment().subtract(6, 'days').format('YYYY-MM-DD'))
    const [end, setEnd] = useState(today)

    const selectMonth = (year, monthIndex) => {
        const s = moment([year, monthIndex, 1]).startOf('month')
        let e = moment([year, monthIndex, 1]).endOf('month')
        if (e.isAfter(now)) e = now.clone()
        setStart(s.format('YYYY-MM-DD'))
        setEnd(e.format('YYYY-MM-DD'))
    }
    const isMonthFuture = (year, i) => moment([year, i, 1]).isAfter(now, 'month')
    const isMonthActive = (year, i) => moment(start).year() === year && moment(start).month() === i && moment(start).date() === 1
    const [status, setStatus] = useState('done')
    const [type, setType] = useState(null)
    const [rows, setRows] = useState(null)
    const [load, setLoad] = useState(false)
    const [err, setErr] = useState('')

    const typeOf = (s) => s.type || 'streaming'

    const fetchOne = (dateStr) => new Promise((resolve) => {
        socket.emit('empire_tracker_list_ask_paiement', { token: userToken.expecttoken, date: new Date(dateStr + 'T12:00:00') }, (e) => resolve(Array.isArray(e) ? e : []))
    })

    const compare = async () => {
        setErr('')
        if (!start || !end) return setErr('Renseigne les deux dates')
        if (moment(end).isAfter(today)) return setErr("La date de fin ne peut pas dépasser aujourd'hui")
        if (moment(start).isAfter(moment(end))) return setErr('La date de début doit être avant la fin')

        setLoad(true)
        const days = []
        let d = moment(start)
        const last = moment(end)
        while (d.isSameOrBefore(last, 'day')) { days.push(d.format('YYYY-MM-DD')); d = d.clone().add(1, 'day') }

        const results = []
        for (const day of days) {
            const groups = await fetchOne(day)
            results.push({ date: day, groups })
        }
        setRows(results)
        setLoad(false)
    }

    const metrics = (row) => {
        const group = (row.groups || []).find(g => g.status === status)
        const data = (group?.data || []).filter(s => !type || typeOf(s) === type)
        const total = data.reduce((a, s) => a + (parseFloat(s.price) || 0), 0)
        return { count: data.length, total }
    }

    const tableRows = rows ? rows.map(r => ({ date: r.date, ...metrics(r) })) : []
    const grandCount = tableRows.reduce((a, r) => a + r.count, 0)
    const grandTotal = tableRows.reduce((a, r) => a + r.total, 0)
    const chartData = tableRows.map(r => ({ date: moment(r.date).format('DD/MM'), total: +r.total.toFixed(2) }))

    return (
        <div className='modal-overlay' onClick={onClose}>
            <div className='modal-box lg' onClick={(e) => e.stopPropagation()}>
                <div className='df aic jcsb mb2'>
                    <p className='fb cw fs18 df aic gap1'><GitCompare size={18} color='#fff' /> Comparateur</p>
                    <button onClick={onClose} className='pay-act-btn'><X size={18} color='#fff' /></button>
                </div>

                {/* Raccourci années / mois */}
                <div className='df gap1 fw mb1 compare-years'>
                    {years.map(y => (
                        <button key={y} onClick={() => setSelectedYear(y)} className={selectedYear === y ? 'active' : ''}>{y}</button>
                    ))}
                </div>
                <div className='df gap1 fw mb2 compare-months'>
                    {MONTHS.map((m, i) => (
                        <button key={i}
                            disabled={isMonthFuture(selectedYear, i)}
                            onClick={() => selectMonth(selectedYear, i)}
                            className={isMonthActive(selectedYear, i) ? 'active' : ''}>{m}</button>
                    ))}
                </div>

                {/* Dates */}
                <div className='df gap1 fw mb2'>
                    <div className='flex1'>
                        <label className='fr cw fs12'>Début</label>
                        <input type='date' max={today} value={start} onChange={(e) => setStart(e.currentTarget.value)} className='premium-date-input mt1' />
                    </div>
                    <div className='flex1'>
                        <label className='fr cw fs12'>Fin</label>
                        <input type='date' max={today} value={end} onChange={(e) => setEnd(e.currentTarget.value)} className='premium-date-input mt1' />
                    </div>
                </div>

                {/* Statut */}
                <div className='df gap1 fw mb1'>
                    {STATUSES.map(s => (
                        <button key={s.key} onClick={() => setStatus(s.key)} className={`btn ${status === s.key ? 'cs' : 'cw'} `}>{s.label}</button>
                    ))}
                </div>
                {/* Type */}
                <div className='df gap1 fw mb2'>
                    {TYPES.map(t => (
                        <button key={t.label} onClick={() => setType(t.key)} className={`btn ${type === t.key ? 'cs' : 'cw'} `}>{t.label}</button>
                    ))}
                </div>

                {err && <p className='ce fr fs12 mb1'>{err}</p>}

                <button onClick={compare} disabled={load} className='btn cs w100 df aic jcc mb2'>
                    {load ? <span className='loader sm'></span> : 'Comparer'}
                </button>

                {rows && !load && (
                    <>
                        <div className='compare-summary df aic jcsb gap1 mb2'>
                            <span className='fr cw'>Total de la période</span>
                            <span className='fb cw'>{grandTotal.toFixed(2)}€ · {grandCount} paiement{grandCount > 1 ? 's' : ''}</span>
                        </div>

                        <div className='compare-chart mb2'>
                            <ResponsiveContainer width='100%' height={260}>
                                <BarChart data={chartData} margin={{ top: 10, right: 10, left: -15, bottom: 0 }}>
                                    <CartesianGrid stroke='rgba(255,255,255,0.06)' vertical={false} />
                                    <XAxis dataKey='date' stroke='#8f909c' fontSize={11} />
                                    <YAxis stroke='#8f909c' fontSize={11} />
                                    <Tooltip cursor={{ fill: 'rgba(255,255,255,0.05)' }} contentStyle={{ background: '#1b1822', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, color: '#fff' }} />
                                    <Bar dataKey='total' fill='#9754e3' radius={[6, 6, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>

                        <div className='compare-table'>
                            <div className='ct-head'>
                                <span>Date</span>
                                <span className='tar'>Nombre</span>
                                <span className='tar'>Total</span>
                            </div>
                            {tableRows.map((r, i) => (
                                <div className='ct-row' key={i}>
                                    <span className='cw'>{moment(r.date).format('DD/MM/YYYY')}</span>
                                    <span className='tar cw'>{r.count}</span>
                                    <span className='tar cw fb'>{r.total.toFixed(2)}€</span>
                                </div>
                            ))}
                            <div className='ct-row ct-total'>
                                <span className='fb cw'>Total</span>
                                <span className='tar fb cw'>{grandCount}</span>
                                <span className='tar fb cw'>{grandTotal.toFixed(2)}€</span>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default ModalComparateur;
